/**
 * Services.jsx
 * Dynamic Services API + Image Version
 */

import { useEffect, useState } from "react";
import { apiFetch } from "../helpers";

const COLORS = [
  { color: "#1D9E75", bg: "#E1F5EE" },
  { color: "#378ADD", bg: "#E6F1FB" },
  { color: "#D85A30", bg: "#FAECE7" },
  { color: "#7F77DD", bg: "#EEEDFE" },
  { color: "#BA7517", bg: "#FAEEDA" },
  { color: "#E24B4A", bg: "#FCEBEB" },
];

export default function Services({ onBookClick }) {
  const [services, setServices] = useState([]);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const json = await apiFetch(
        `${process.env.REACT_APP_GET_ALL_SERVICES}`
      );

      if (json.status) {
        const rows = (json.data || []).map((item, i) => ({
          id: item.service_id,
          title: item.service_name,
          desc: item.description,
          price: item.price,
          image: item.image,
          ...COLORS[i % COLORS.length],
        }));

        setServices(rows);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const imageUrl = (img) => {
    if (!img) return "";

    const cleanPath = img.replace(/\\/g, "/");

    const baseUrl =process.env.REACT_APP_WEB_URL;
    return `${baseUrl}/${cleanPath.replace(/^\/+/, "")}`;
  };

  return (
    <section
      id="services"
      style={{
        background: "#EEF2F7",
        padding: "90px 0 80px",
        fontFamily: "'Nunito','Segoe UI',sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "0 1.5rem",
        }}
      >
        {/* Heading */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 56,
          }}
        >
          <span
            style={{
              display: "inline-block",
              background: "#E6F1FB",
              color: "#042C53",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              padding: "5px 16px",
              borderRadius: 20,
              marginBottom: 14,
            }}
          >
            Our Services
          </span>

          <h2
            style={{
              fontSize:
                "clamp(1.6rem, 4vw, 2.5rem)",
              fontWeight: 800,
              color: "#0A1628",
              margin: "0 0 16px",
            }}
          >
            Comprehensive Dental
            <br />
            <span style={{ color: "#1D9E75" }}>
              Treatments
            </span>
          </h2>

          <p
            style={{
              color: "#6B7A8D",
              fontSize: 16,
              maxWidth: 500,
              margin: "0 auto",
              lineHeight: 1.7,
            }}
          >
            From routine cleanings to full smile
            transformations — we offer everything
            your family's dental health needs under
            one roof.
          </p>
        </div>

        {/* Grid */}
        <div
          className="services-grid"
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(4, 1fr)",
            gap: 18,
          }}
        >
          {services.map((s, i) => (
            <div
              key={i}
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: "24px 20px",
                border: "1px solid #E4EAF0",
                transition: ".25s",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-5px)";
                e.currentTarget.style.boxShadow = `0 12px 32px ${s.color}22`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform =
                  "none";
                e.currentTarget.style.boxShadow =
                  "none";
              }}
            >
              {/* Top Line */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: s.color,
                }}
              />

              {/* Image */}
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 12,
                  background: s.bg,
                  overflow: "hidden",
                  marginBottom: 14,
                }}
              >
                {s.image && (
                  <img
                    src={imageUrl(s.image)}
                    alt={s.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) =>
                      (e.target.style.display =
                        "none")
                    }
                  />
                )}
              </div>

              {/* Title */}
              <h4
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#0A1628",
                  marginBottom: 8,
                }}
              >
                {s.title}
              </h4>

              {/* Desc */}
              <p
                style={{
                  fontSize: 12,
                  color: "#6B7A8D",
                  lineHeight: 1.65,
                  marginBottom: 16,
                }}
              >
                {s.desc}
              </p>

              {/* Price */}
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: s.color,
                  }}
                >
                  From ₹{s.price}
                </span>

                <button
                  onClick={onBookClick}
                  style={{
                    background: s.bg,
                    color: s.color,
                    border: `1px solid ${s.color}40`,
                    borderRadius: 8,
                    padding: "6px 12px",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Book Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Button */}
        <div
          style={{
            textAlign: "center",
            marginTop: 48,
          }}
        >
          <button
            onClick={onBookClick}
            style={{
              background: "#E8323C",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "14px 36px",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            📅 Book an Appointment
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width:1024px){
          .services-grid{
            grid-template-columns:repeat(2,1fr)!important;
          }
        }

        @media (max-width:576px){
          .services-grid{
            grid-template-columns:1fr!important;
          }
        }
      `}</style>
    </section>
  );
}